import { useState } from 'react';
import { useWSMessage } from '../context/WSMessageContext';
import useWSFeedbackMessages from '../hooks/useWSFeedbackMessages';
import useWSMessageSender from '../hooks/useWSMessageSender';
import useWSMessageTicks from '../hooks/useWSMessageTicks';
import useWSTurnState, { TURN_ACTIONS } from '../hooks/useWSTurnState';

export default function ChatTest() {
  const [input, setInput] = useState('');
  const [wordsToComplete, setWordsToComplete] = useState('');
  const [completedWords, setCompletedWords] = useState('');
  const { isConnected, error } = useWSMessage();
  const { messages: messageMessages, activeMessage } = useWSFeedbackMessages();
  const { messages: tickMessages, activeTick } = useWSMessageTicks();
  const { sendTranscription, sendTurnAction } = useWSMessageSender();
  const { currentPhase, lastCompleter } = useWSTurnState();

  const handlesendTranscription = () => {
    if (sendTranscription(input)) {
      setInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlesendTranscription();
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Status da conexão */}
      <div className={`text-sm ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
        {isConnected ? '🟢 Conectado' : '🔴 Desconectado'}
        {error && <span className="text-red-600"> - Erro: {error}</span>}
      </div>

      {/* Área de mensagens */}
      <div className="border p-2 h-64 overflow-y-auto rounded bg-gray-50">
        {messageMessages.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            Aguardando mensagens...
          </div>
        ) : (
          messageMessages.map((msg, idx) => (
            <div key={`${idx}-${msg.content}`} className="p-1 border-b">
              {idx + 1}: [{msg.type}] {msg.content}
            </div>
          ))
        )}
      </div>

      {/* (removido) Área de correções de continuação */}

      {/* Input e botão */}
      <div className="flex space-x-2">
        <input
          className="flex-1 border rounded px-2 py-1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Digite sua mensagem..."
          disabled={!isConnected}
        />
        <button
          type="button"
          className={`px-3 py-1 rounded text-white ${
            isConnected
              ? 'bg-blue-500 hover:bg-blue-600'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
          onClick={handlesendTranscription}
          disabled={!isConnected}
        >
          Enviar
        </button>
      </div>

      {/* Status das mensagens ativas */}
      <div className="text-sm text-green-600">
        Mensagem ativa: {activeMessage ? `[${activeMessage.type}] ${activeMessage.content}` : 'Nenhuma mensagem ativa'}
      </div>

      {/* Status dos ticks */}
      <div className="text-sm text-blue-600">
        Tick ativo: {activeTick ? `[${activeTick.type}] ${activeTick.content}` : 'Nenhum tick ativo'}
      </div>

      {/* (removido) Status/Debug de correções de continuação */}

      {/* Debug dos Turnos */}
      <div className="border-t pt-4 mt-4">
        <h3 className="text-lg font-semibold mb-3">🎮 Debug dos Turnos</h3>

        {/* Estado Atual */}
        <div className="bg-gray-100 p-3 rounded mb-4">
          <div className="text-sm font-medium mb-2">Estado Atual:</div>
          <div className="text-sm space-y-1">
            <div>
              <span className="font-medium">Fase:</span> {currentPhase}
            </div>
            <div>
              <span className="font-medium">Último Completador:</span> {lastCompleter || 'Nenhum'}
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="mb-4">
          <div className="text-sm font-medium mb-2">Ações Disponíveis:</div>
          <div className="grid grid-cols-5 gap-2">
            <button
              type="button"
              onClick={() => sendTurnAction({
                action: TURN_ACTIONS.ACompleted,
                spokenContinuation: "debug test phrase for A"
              })}
              className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
            >
              A Completed
            </button>
            <button
              type="button"
              onClick={() => sendTurnAction({
                action: TURN_ACTIONS.BCompleted,
                spokenContinuation: "debug test phrase for B"
              })}
              className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
            >
              B Completed
            </button>
            <button
              type="button"
              onClick={() => sendTurnAction({
                action: TURN_ACTIONS.AAsked,
                spokenContinuation: "debug ask from A"
              })}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              A Asked
            </button>
            <button
              type="button"
              onClick={() => sendTurnAction({
                action: TURN_ACTIONS.BAsked,
                spokenContinuation: "debug ask from B"
              })}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              B Asked
            </button>
            <button
              type="button"
              onClick={() => sendTurnAction({
                action: TURN_ACTIONS.AwkwardSilence,
                spokenContinuation: "debug awkward silence"
              })}
              className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
            >
              Awkward Silence
            </button>
          </div>
        </div>

        {/* Participantes */}
        <div className="bg-gray-100 p-3 rounded">
          <div className="text-sm font-medium mb-2">Participantes:</div>
          <div className="text-xs text-gray-600">
            <div>Participante A: <span className="text-blue-600">Ativo</span></div>
            <div>Participante B: <span className="text-blue-600">Ativo</span></div>
          </div>
        </div>
      </div>

      {/* Simulador de Completar Palavras */}
      <div className="border-t pt-4 mt-4">
        <h3 className="text-lg font-semibold mb-3">🧩 Simulador de Completar Palavras</h3>

        <div className="space-y-4">
          {/* Input para palavras que devem ser completadas */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Palavras que devem ser completadas:
            </label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={wordsToComplete}
              onChange={(e) => setWordsToComplete(e.target.value)}
              placeholder="Ex: I love programming because..."
              disabled={!isConnected}
            />
          </div>

          {/* Input para simular o que a pessoa completou */}
          <div>
            <label className="block text-sm font-medium mb-2">
              O que a pessoa completou:
            </label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={completedWords}
              onChange={(e) => setCompletedWords(e.target.value)}
              placeholder="Ex: it is fun and challenging"
              disabled={!isConnected}
            />
          </div>

          {/* Botão para simular envio */}
          <div className="flex space-x-2">
            <button
              type="button"
              className={`px-4 py-2 rounded text-white ${
                isConnected && wordsToComplete && completedWords
                  ? 'bg-purple-500 hover:bg-purple-600'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              disabled={!isConnected || !wordsToComplete || !completedWords}
              onClick={() => {
                console.log('🎯 Simulando completar palavras:', {
                  wordsToComplete,
                  completedWords
                });

                // (removido) Envio de continuation
              }}
            >
              Simular Completar
            </button>

            <button
              type="button"
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              onClick={() => {
                setWordsToComplete('');
                setCompletedWords('');
              }}
            >
              Limpar
            </button>
          </div>

          {/* Preview dos dados */}
          {(wordsToComplete || completedWords) && (
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-sm font-medium mb-2">Preview:</div>
              <div className="text-sm space-y-1">
                <div>
                  <span className="font-medium">Frase original:</span> {wordsToComplete || 'Nenhuma'}
                </div>
                <div>
                  <span className="font-medium">Completado:</span> {completedWords || 'Nenhuma'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-600">
        Total de mensagens MESSAGE: {messageMessages.length} | Total de ticks: {tickMessages.length}
      </div>
    </div>
  );
}
